import type { DeckPlayerBootstrapV1, DeckPlayerShortcodeProps } from '../types'

const toFlag = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    if (v === 'true' || v === '1' || v === 'yes' || v === 'on') return true
    if (v === 'false' || v === '0' || v === 'no' || v === 'off') return false
  }
  return undefined
}

/**
 * Page builders (e.g. Elementor) serialize unchecked toggles as explicit `false`. For
 * template-style settings that means "unset", so deck/card defaults still apply.
 */
const toTruthyFlag = (value: unknown): true | undefined =>
  toFlag(value) === true ? true : undefined

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return undefined
}

const toText = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : typeof value === 'number' ? String(value) : undefined

const toColor = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length ? value : undefined

/** First defined value among the given attribute aliases. */
const first = <T>(
  read: (value: unknown) => T | undefined,
  raw: Record<string, unknown>,
  keys: string[],
) => {
  for (const key of keys) {
    const value = read(raw[key])
    if (value !== undefined) return value
  }
  return undefined
}

/**
 * Player options from shortcode/widget attributes, accepting legacy and snake_case
 * aliases and string/number encodings of flags and numbers.
 */
export function coercePlayerProps(
  input: Record<string, unknown> | undefined,
): DeckPlayerShortcodeProps {
  const raw = input ?? {}
  const flag = (...keys: string[]) => first(toFlag, raw, keys)
  const truthy = (...keys: string[]) => first(toTruthyFlag, raw, keys)
  const text = (...keys: string[]) => first(toText, raw, keys)

  return {
    showControls: flag('showControls', 'controls'),
    showProgressBar: flag('showProgressBar', 'bar'),
    showCount: flag('showCount', 'count'),
    showDeckTitle: flag('showDeckTitle', 'title'),
    allowSkip: flag('allowSkip', 'skip'),
    showReset: flag('showReset', 'reset'),

    showNext: flag('showNext', 'next'),
    showBack: flag('showBack', 'back'),
    showUndo: flag('showUndo', 'undo'),
    showRedo: flag('showRedo', 'redo'),

    disableAutoplay: truthy(
      'disableAutoplay',
      'disable_autoplay',
      'disableAudioAutoplay',
      'disable_audio_autoplay',
    ),

    cardChangeAnimation: text(
      'cardChangeAnimation',
      'cardTransition',
      'animation',
    ) as DeckPlayerShortcodeProps['cardChangeAnimation'],
    successCardChangeAnimation: text(
      'successCardChangeAnimation',
      'successAnimation',
      'success_animation',
    ) as DeckPlayerShortcodeProps['successCardChangeAnimation'],
    failedCardChangeAnimation: text(
      'failedCardChangeAnimation',
      'failedAnimation',
      'failed_animation',
      'failAnimation',
      'fail_animation',
    ) as DeckPlayerShortcodeProps['failedCardChangeAnimation'],

    // Display scaling
    scale: toNumber(raw.scale),
    ignoreBound: flag('ignoreBound', 'ignore_bound'),

    // Forwarded Flashcard props
    width: toNumber(raw.width),
    height: toNumber(raw.height),
    darkMode: truthy('darkMode', 'dark_mode'),
    backgroundColor: toColor(raw.backgroundColor),
    themeBackgroundColor: toColor(raw.themeBackgroundColor),
    borderRadius: toNumber(raw.borderRadius),
    themeBorderRadius: toNumber(raw.themeBorderRadius),
    borderColor: toColor(raw.borderColor),
    themeBorderColor: toColor(raw.themeBorderColor),
    borderWidth: toNumber(raw.borderWidth),
    shadow: toText(raw.shadow),
    transitionDuration: toNumber(raw.transitionDuration),
    themeTransitionDuration: toNumber(raw.themeTransitionDuration),
    flipAnimation: toText(raw.flipAnimation),
    themeFlipAnimation: toText(raw.themeFlipAnimation),
    hoverFlip: truthy('hoverFlip', 'hover_flip'),
    showBackFirst: truthy('showBackFirst', 'show_back_first'),
    points: toNumber(raw.points),
    pearls: toNumber(raw.pearls),
    showPoints: flag('showPoints', 'show_points'),
    showPearls: flag('showPearls', 'show_pearls'),
    titleOverride: toText(raw.titleOverride),
  }
}

/** Attribute values win; otherwise the logged-in user's rewards and display settings. */
export function withUserRewards(
  props: DeckPlayerShortcodeProps,
  user: DeckPlayerBootstrapV1['user'] | undefined,
): DeckPlayerShortcodeProps {
  return {
    ...props,
    points: props.points ?? user?.points,
    pearls: props.pearls ?? user?.pearls,
    showPoints:
      props.showPoints ??
      user?.rewardDisplay?.deckplayer_points ??
      user?.rewardDisplay?.show_points !== false,
    showPearls:
      props.showPearls ??
      user?.rewardDisplay?.deckplayer_pearls ??
      user?.rewardDisplay?.show_pearls !== false,
  }
}

/** The shortcode `debug` (or `pfcDebug`) attribute as a boolean. */
export function isDebugEnabled(passthrough: Record<string, unknown> | undefined): boolean {
  const raw = passthrough ?? {}
  const value = raw.debug ?? raw.pfcDebug
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    return v === '1' || v === 'true' || v === 'yes' || v === 'on'
  }
  return false
}
